import React from "react";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";
import { FaDownload } from "react-icons/fa";

const LeadAuditExcel = ({
  excelData = {}, // Expecting an object
  sheetName = sheetName || [
    "Unit Details",
    "Audit details"
  ],
  fileName = "Report",
  className = "",
  exportPosition,
  correctionDate,
  color = "FF0000", // Default red header
  focusArea = [], // New prop for Focus Area
  additionalAttendees = [], // New prop for Additional Attendees
  unitDetails,
}) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  // Define column widths
  const wscols = [
    { wch: 25 }, // audit_criteria
    { wch: 20 }, // audit_delivery
    { wch: 15 }, // audit_start_date
    { wch: 15 }, // audit_end_date
    { wch: 15 }, // audit_language
    { wch: 15 }, // audit_type
    { wch: 20 }, // cb_office_location
    { wch: 10 }, // is_fssc_conjunction
    { wch: 15 }, // lead_auditor
    { wch: 10 }, // version
    { wch: 15 }, // focus_area
    { wch: 50 }, // additional_attendees
  ];

  const columnWidth = { wch: 20 };

  // Apply styles to the sheet
  const addStyles = (workSheet, color) => {
    // Apply styles to the header row (columns A-Z, AA, AB, etc.)
    Object.entries(workSheet)
      .filter(([k]) => k.match(/^[A-Z]{1,2}1$/)) // Target all header rows (1st row of each column, including multi-letter columns)
      .forEach(([k, v]) => {
        v.s = {
          fill: { fgColor: { rgb: color } }, // Header background color
          alignment: { vertical: "center", horizontal: "left" },
          font: { bold: true, color: { rgb: "FFFFFF" } }, // White text
        };
      });

    // Apply styles to the data rows (columns A-Z, AA, AB, etc.)
    Object.entries(workSheet)
      .filter(([k]) => k.match(/^[A-Z][2-9]\d*$/)) // Apply styles for other rows (excluding header row)
      .forEach(([k, v]) => {
        v.s = {
          alignment: { vertical: "center", horizontal: "left", wrapText: true },
        };
      });

    // Set the same column width for all columns
    const allColumns = Object.keys(workSheet);
    allColumns.forEach((column) => {
      if (column.match(/^[A-Z]{1,2}\d+/)) {
        // This ensures we only apply width to actual columns (excluding metadata)
        workSheet["!cols"] = workSheet["!cols"] || [];
        workSheet["!cols"].push(columnWidth);
      }
    });
  };

  function formatDate(dateStr) {
    const date = new Date(dateStr);

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const day = String(date.getDate()).padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    // Return the formatted date
    return `${day}-${month}-${year}`;
  }

  const exportToExcel = () => {
    if (
      (!excelData || Object.keys(excelData).length === 0) &&
      (!unitDetails || Object.keys(unitDetails).length === 0)
    ) {
      console.error("No data to export");
      return;
    }

    // Format Focus Area as a comma-separated string
    const formattedFocusArea =
      focusArea.length > 0 ? focusArea.join(", ") : "N/A";

    // Format Additional Attendees as a string like "Arvind: Team Member, Naba: Witness"
    const formattedAdditionalAttendees =
      additionalAttendees.length > 0
        ? additionalAttendees
            .map((attendee) => `${attendee.name}: ${attendee.function}`)
            .join(", ")
        : "N/A";

        const formattedUnitDetails = unitDetails?.reduce((acc, item) => {
          acc[item.label] = item.value || "N/A";
          return acc;
        }, {});

    let formattedData;

    if (correctionDate) {
      formattedData = [
        {
          "Audit Criteria":
            JSON.parse(excelData.audit_criteria || "[]").join(", ") || "N/A", // Convert string to array
          "Audit Delivery": excelData.audit_delivery || "N/A",
          "Audit Start Date": excelData.audit_start_date
            ? formatDate(excelData.audit_start_date)
            : "N/A",
          "Audit End Date": excelData.audit_end_date
            ? formatDate(excelData.audit_end_date)
            : "N/A",
          "Audit Language": excelData.audit_language || "N/A",
          "Audit Type":
            excelData.audit_type === "sustaining"
              ? "Sustaining"
              : excelData.audit_type === "gap assessment"
              ? "Gap Assesment"
              : "N/A",
          "CB Office Location": excelData.cb_office_location || "N/A",
          "Is FSSC Conjunction":
            excelData.is_fssc_conjunction === true
              ? "Yes"
              : excelData.is_fssc_conjunction === false
              ? "No"
              : "N/A",
          "Lead Auditor": excelData.lead_auditor || "N/A",
          Version: excelData.version || "N/A",
          "Job Number": excelData.jobnumber || "N/A",
          "Corrective Action Date": correctionDate
            ? formatDate(correctionDate)
            : "N/A",
          "Focus Area": formattedFocusArea,
          "Additional Attendees": formattedAdditionalAttendees,
        },
      ];
    } else {
      formattedData = [
        {
          "Audit Criteria":
            JSON.parse(excelData.audit_criteria || "[]").join(", ") || "N/A", // Convert string to array
          "Audit Delivery": excelData.audit_delivery || "N/A",
          "Audit Start Date": excelData.audit_start_date
            ? formatDate(excelData.audit_start_date)
            : "N/A",
          "Audit End Date": excelData.audit_end_date
            ? formatDate(excelData.audit_end_date)
            : "N/A",
          "Audit Language": excelData.audit_language || "N/A",
          "Audit Language": excelData.audit_language || "N/A",
          "Audit Type":
            excelData.audit_type === "sustaining"
              ? "Sustaining"
              : excelData.audit_type === "gap assessment"
              ? "Gap Assesment"
              : "N/A",

          "CB Office Location": excelData.cb_office_location || "N/A",
          "Is FSSC Conjunction":
            excelData.is_fssc_conjunction === true
              ? "Yes"
              : excelData.is_fssc_conjunction === false
              ? "No"
              : "N/A",
          "Lead Auditor": excelData.lead_auditor || "N/A",
          Version: excelData.version || "N/A",
          "Job Number": excelData.jobnumber || "N/A",
          "Focus Area": formattedFocusArea,
          "Additional Attendees": formattedAdditionalAttendees,
        },
      ];
    }

    const ws = XLSX.utils.json_to_sheet(formattedData);
    ws["!cols"] = wscols;
    const unitDetailSheet = XLSX.utils.json_to_sheet([formattedUnitDetails]);
    addStyles(ws,color);
    addStyles(unitDetailSheet, color);

    // Create workbook
    const wb = {
      Sheets: { [sheetName[0]]: unitDetailSheet, [sheetName[1]]: ws },
      SheetNames: [sheetName[0], sheetName[1]],
    };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });

    FileSaver.saveAs(data, fileName + fileExtension);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "8px",
        cursor: "pointer",
        position: exportPosition ? "absolute" : "unset",
        top: "0px",
        right: "12px",
      }}
      title="Export in Excel"
      onClick={exportToExcel}
      className={`${className} bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 transition duration-300` }

    >
      <FaDownload color="#ffffff" size={16} />
      <p
        style={{
          marginBottom: "0",
          color: "#ffffff",
          fontSize: "16px",
          fontWeight: "500",
        }}
      >
        Export Data
      </p>
    </div>
  );
};

export default LeadAuditExcel;
